/**
 * Structured JSON logger for Kairos.
 *
 * Usage:
 *   const log = createLogger('MyService');
 *   log.info('load created', { loadId, schemaName: 'LoadSchema' });
 *
 * Output (stdout / console.log):
 *   {"ts":"2026-04-23T05:00:00.000Z","level":"info","module":"MyService",
 *    "message":"load created","tenantId":"...","correlationId":"...","loadId":"...","schemaName":"LoadSchema"}
 *
 * Rules (OBS-1 arch):
 *   - Every output line is a JSON object. No free-form strings.
 *   - Never log payloads. Log schemaName, docId, reasonCode only.
 *   - Fields whose names match the redaction pattern become "[REDACTED]".
 *   - LOG_LEVEL (process.env) or VITE_LOG_LEVEL (import.meta.env) controls filtering.
 */

import { getContext } from './context.js';

const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

// Any field name containing these substrings (case-insensitive) is redacted.
const REDACT_RE = /key|token|secret|password|credential|authorization/i;

function redact(obj, depth = 0) {
  if (depth > 4 || obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(v => redact(v, depth + 1));
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = REDACT_RE.test(k) ? '[REDACTED]' : redact(v, depth + 1);
  }
  return out;
}

function resolveLevel() {
  // process.env takes priority — works in Node (functions) and Vitest
  if (typeof process !== 'undefined' && process.env?.LOG_LEVEL) {
    return process.env.LOG_LEVEL.toLowerCase();
  }
  if (import.meta.env?.VITE_LOG_LEVEL) {
    return import.meta.env.VITE_LOG_LEVEL.toLowerCase();
  }
  return import.meta.env?.DEV ? 'debug' : 'info';
}

function emit(level, moduleName, message, payload) {
  if ((LEVELS[level] ?? 0) < (LEVELS[resolveLevel()] ?? 1)) return;

  const ctx = getContext();
  const { tenantId: pTenant, correlationId: pCid, ...rest } = payload;

  const entry = {
    ts: new Date().toISOString(),
    level,
    module: moduleName,
    message,
  };

  const tenantId = ctx.tenantId ?? pTenant;
  const correlationId = ctx.correlationId ?? pCid;
  if (tenantId !== undefined) entry.tenantId = tenantId;
  if (correlationId !== undefined) entry.correlationId = correlationId;

  Object.assign(entry, redact(rest));

  // Firebase Functions captures stdout as Cloud Logging entries.
  console.log(JSON.stringify(entry));
}

export function createLogger(moduleName) {
  return {
    debug: (message, payload = {}) => emit('debug', moduleName, message, payload),
    info:  (message, payload = {}) => emit('info',  moduleName, message, payload),
    warn:  (message, payload = {}) => emit('warn',  moduleName, message, payload),
    error: (message, payload = {}) => emit('error', moduleName, message, payload),
  };
}
